import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import labourMasterApis from "../../../api/master/labourMaster";
import useSequence from "../../../hooks/useSequence";

interface SelectOptions {
  label: string;
  value: string | number;
}
interface LabourPayload {
  laborCategoryName: string;
  description?: string;
  laborTypeId: any;
  laborCategorySequenceId?: string;
}

const LabourMasterForm: FC<{
  id?: string | null;
  formAction?: string | null;
  toggleModelPopUp: () => void;
  refreshData?: () => void;
}> = ({ id, formAction, toggleModelPopUp, refreshData }) => {
  const [action, setAction] = useState(formAction);
  const [labourDetail, setLabourDetail] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [laborTypes, setLaborTypes] = useState<SelectOptions[]>([]);
  const [alertInfo, setAlertInfo] = useState<{
    type: string;
    message: string;
    show: boolean;
  }>({
    type: "",
    message: "",
    show: false,
  });

  const { hasSequence } = useSequence(171);
  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, watch, getValues } = methods;

  const formValues = watch();

  const fetchLaborTypes = async () => {
    try {
      const response = await labourMasterApis.labourTypesList();
      if (response?.status) {
        const types = response.data.map((type: any) => ({
          label: type.laborType,
          value: type.laborTypeId,
        }));
        setLaborTypes(types);
      }
    } catch (error) {
      console.error("Error fetching labor types:", error);
    }
  };

  const fetchLabourDetails = async (labourId: any) => {
    try {
      setLoading(true);
      const response = await labourMasterApis.fetchLabourById(labourId);
      if (response?.status) {
        const data = response.data;
        setLabourDetail(data);
        setValue("laborCategorySequenceId", data.laborCategoryId);
        setValue("laborCategoryName", data.laborCategoryName);
        setValue("description", data.description || "");
        setValue("laborTypeId", data.laborTypeId);
      } else {
        showAlertMessage("error", "Failed to load labour category details.");
      }
    } catch (error) {
      showAlertMessage("error", "Error fetching labour details.");
    } finally {
      setLoading(false);
    }
  };

  const showAlertMessage = (type: string, message: string) => {
    setAlertInfo({ type, message, show: true });
  };

  const hideAlert = () => {
    setAlertInfo((prev) => ({ ...prev, show: false }));
  };

  const hasFormChanges = () => {
    if (action !== "edit" || !id) return true;
    if (!labourDetail || Object.keys(labourDetail).length === 0) return true;
    const current = getValues();
    const currentValues = {
      laborCategoryName: current.laborCategoryName || "",
      description: current.description || "",
      laborTypeId: Number(current.laborTypeId || "") || "",
    };

    const originalValues = {
      laborCategoryName: labourDetail.laborCategoryName || "",
      description: labourDetail.description || "",
      laborTypeId: Number(labourDetail.laborTypeId || "") || "",
    };

    return (
      currentValues.laborCategoryName !== originalValues.laborCategoryName ||
      currentValues.description !== originalValues.description ||
      currentValues.laborTypeId !== originalValues.laborTypeId
    );
  };

  const onSubmit = async (_data: any) => {
    try {
      setLoading(true);
      hideAlert();
      const current = getValues();

      const submitData: LabourPayload = {
        laborCategoryName: current.laborCategoryName || "",
        description: current.description || "",
        laborTypeId: current.laborTypeId,
      };

      if (!hasSequence && action !== "edit") {
        submitData.laborCategorySequenceId = current.laborCategorySequenceId;
      }

      if (action === "edit" && id) {
        if (
          labourDetail &&
          Object.keys(labourDetail).length > 0 &&
          !hasFormChanges()
        ) {
          showAlertMessage("error", "No Changes. No fields were modified.");
          setTimeout(() => hideAlert(), 1500);
          setLoading(false);
          return;
        }
      }

      let response;

      if (action === "edit" && id) {
        response = await labourMasterApis.updateLabourById(
          Number(id),
          submitData
        );
      } else {
        response = await labourMasterApis.createLabour(submitData);
      }

      if (response?.status) {
        showAlertMessage(
          "success",
          action === "edit"
            ? "Labour category has been updated"
            : "Labour category has been created"
        );

        setTimeout(() => {
          toggleModelPopUp();
          refreshData?.();
        }, 1500);
      } else {
        const msg = response?.message || "Something went wrong!";
        showAlertMessage("error", msg);
        setTimeout(() => hideAlert(), 1500);
      }
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message;
      showAlertMessage("error", errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLaborTypes();
    if (id && action === "edit") {
      fetchLabourDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, action]);

  useEffect(() => {
    return () => {
      hideAlert();
    };
  }, []);

  if (
    loading &&
    action === "edit" &&
    id &&
    (!labourDetail || Object.keys(labourDetail).length === 0)
  ) {
    return (
      <div className="text-center p-4">
        <Icon name="spinner" className="spin" /> Loading...
      </div>
    );
  }

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? `Edit ${labourDetail.laborCategoryName || "Labour Category"}`
          : "Create New Labour Category"}
      </h5>

      <Alert
        color={alertInfo.type === "error" ? "danger" : "success"}
        className="alert-icon"
        isOpen={alertInfo.show}
        style={{
          textAlign: "left",
          fontSize: "14px",
          cursor: "default",
          marginBottom: "1rem",
          marginTop: "0.5rem",
        }}
      >
        <Icon
          name={alertInfo.type === "error" ? "alert-circle" : "check-circle"}
        />
        <span>{alertInfo.message}</span>
      </Alert>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {!hasSequence && (
              <Col md={6}>
                <FormInput
                  name="laborCategorySequenceId"
                  label="Labour ID"
                  required={true}
                  type="text"
                  pattern={/^[a-zA-Z0-9./_-]+$/}
                  maxLength={30}
                  disabled={action === "edit"}
                  placeholder="Enter Labour ID"
                />
              </Col>
            )}

            <Col md={6}>
              <FormSelect
                name="laborTypeId"
                label="Type"
                options={laborTypes}
                required={true}
                placeholder="Select Type"
                disabled={action === "edit"}
              />
            </Col>

            <Col md={6} className="mt-2">
              <FormInput
                name="laborCategoryName"
                label="Labour Category Name"
                type="text"
                placeholder="Enter Labour Category Name"
                required={true}
                maxLength={100}
              />
            </Col>

            <Col md="12" className="mt-2">
              <FormInput
                name="description"
                label="Description"
                type="textarea"
                placeholder="Write your description"
                required={false}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    <span>{action === "edit" ? "Update" : "Submit"}</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="light"
                    outline
                    onClick={(ev) => {
                      ev.preventDefault();
                      toggleModelPopUp();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </>
  );
};

export default LabourMasterForm;
